import {Section, SectionItem, TooltipInfo} from 'common/components/items-menu/types';
import rollingLineSvg from 'common/assets/images/charts/rolling-line.svg';
import rollingStackedLineSvg from 'common/assets/images/charts/rolling-stacked-line.svg';
import cumulativeLineSvg from 'common/assets/images/charts/cumulative-line.svg';
import cumulativeStackedLineSvg from 'common/assets/images/charts/cumulative-stacked-line.svg';
import {findSelectedItems} from 'common/components/items-menu/utils';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {
  AdvancedMenuValue,
  Calculation,
  CalculationTypes,
  CountBy,
  CountType,
  InsightsCountType,
  isMetricEntry,
  MetricEntry,
  QueryMetricEntry,
} from 'common/query-builder/query-entry-section/types';
import {getSelectedGroupLabel} from 'common/query-builder/query-entry-section/util';
import {AggregationGroupLabel} from 'common/query-builder/types/shared-types';
import {ALL_MATH_TYPES, COUNT_USERS_ONCE_TYPE, CountUsersOnceState, MATH_TYPE} from 'common/report/constants';
import {getIconForPropertyType} from 'common/report/util/properties';
import {toTitleCase} from 'common/util/string';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {RangeSelection} from 'common/widgets/step-range-selector/types';
import {ChartPlotStyle} from 'common/report/insights/util/types';
import {RollingWindowSize} from 'common/util';
import {ROLLING_WINDOW_SIZE_OPTIONS} from 'common/report/insights/models/constants';
import {Segmentation} from 'common/types/reports/query';

import {MetricType} from '../constants';
import {isProperty} from '../properties-menu/types';
import {PropertySectionItem} from '../properties-menu/item-menu-types';
import {getSelectedPropertyName} from '../utils/property-util';
import {
  AggregationSelector,
  COUNT_USERS_ONCE_OPTIONS,
  CountOutputLabel,
  CountOutputLabelMappings,
  CountOutputLabelParams,
  CountSelections,
  CountTypeSelector,
  GroupLabelParams,
  INSIGHTS_COUNT_MENU_ITEMS,
  InsightsCountSubMenuHeader,
  OperatorToString,
  RequiredSelectionsParams,
  SelectorLabel,
  SubSelectionTypes,
} from './constants';

const DEFAULT_ICON = `type-number`;

export function isAdvancedValueCalculation(value: AdvancedMenuValue): value is Calculation {
  return Object.prototype.hasOwnProperty.call(value, `calculationType`);
}
export function isAdvancedValueRangeSelection(value: AdvancedMenuValue): value is RangeSelection {
  return Object.prototype.hasOwnProperty.call(value, `range`);
}
export function isAdvancedValueNumber(value: AdvancedMenuValue): value is number {
  return typeof value === `number`;
}

export function getPropertyName(
  property: PropertySectionItem,
  {
    includeListItemGroup = false,
    showSpecialDatePropertyUnit = false,
  }: {
    includeListItemGroup?: boolean;
    showSpecialDatePropertyUnit?: boolean;
  } = {},
): string {
  if (!property) return ``;
  if (!isProperty(property)) {
    return property.label;
  }
  return getSelectedPropertyName({
    selected: property,
    showSpecialDatePropertyUnit,
    expandListProperties: includeListItemGroup,
  });
}

export function getPropertyIcon(property: PropertySectionItem): string {
  if (isProperty(property)) {
    const type = property?.joinPropertyType ?? property?.type ?? null;
    if (type) {
      return getIconForPropertyType(type, {isCustom: !!property.customProperty});
    }
    return DEFAULT_ICON;
  }
  return DEFAULT_ICON;
}

export function getNestedPropertyIcon(nestedProperty: Segmentation) {
  const type = nestedProperty?.propertyType;
  if (type) {
    return getIconForPropertyType(type, {isCustom: false});
  }
  return DEFAULT_ICON;
}

export function getAggregationIcon(
  countTypeSelection: SectionItem<CountTypeSelector>,
  selectionType: SubSelectionTypes,
): string {
  const {countType} = countTypeSelection.value;
  if (countType === InsightsCountType.UniqueUsers || selectionType === SubSelectionTypes.AggregationPerUser) {
    return `team`;
  }
  return `aggregate`;
}

export function formatSelectorLabel(
  label: SelectorLabel,
  {dataGroupId, dataGroups, timePeriod}: {dataGroupId: string; dataGroups: DataGroup[]; timePeriod?: string},
) {
  if (typeof label === `function`) {
    const replacements: GroupLabelParams = {
      [AggregationGroupLabel.Group]: dataGroupId ? getSelectedGroupLabel({dataGroupId, dataGroups}) : `user`,
      [AggregationGroupLabel.GroupTitle]: toTitleCase(getSelectedGroupLabel({dataGroupId, dataGroups})),
      [AggregationGroupLabel.PluralGroup]: dataGroupId
        ? getSelectedGroupLabel({dataGroupId, dataGroups, isPlural: true})
        : `users`,
      [AggregationGroupLabel.PluralGroupTitle]: toTitleCase(
        getSelectedGroupLabel({dataGroupId, dataGroups, isPlural: true}),
      ),
      [AggregationGroupLabel.TimePeriod]: timePeriod,
    };
    return label(replacements);
  }
  return label;
}

function formatOutputLabel({
  label,
  aggregationOperator,
  aggregationOperatorPerUser,
  countUsersOnceType,
  property,
  dataGroupId,
  dataGroups,
  timePeriod,
}: {
  label: CountOutputLabel;
  aggregationOperator: MATH_TYPE;
  aggregationOperatorPerUser: MATH_TYPE;
  countUsersOnceType: COUNT_USERS_ONCE_TYPE;
  property: PropertySectionItem;
  dataGroupId: string;
  dataGroups: DataGroup[];
  timePeriod: string;
}): string {
  if (typeof label === `function`) {
    const replacements: CountOutputLabelParams = {
      [CountSelections.AggregationOperator]: aggregationOperator,
      [CountSelections.AggregationOperatorPerUser]: aggregationOperatorPerUser,
      [CountSelections.CountUsersOnceType]: countUsersOnceType,
      [CountSelections.PropertyName]: getPropertyName(property, {
        includeListItemGroup: true,
        showSpecialDatePropertyUnit: true,
      }),
      [CountSelections.PropertyValue]: property?.value,
      [AggregationGroupLabel.Group]: dataGroupId ? getSelectedGroupLabel({dataGroupId, dataGroups}) : `user`,
      [AggregationGroupLabel.GroupTitle]: toTitleCase(getSelectedGroupLabel({dataGroupId, dataGroups})),
      [AggregationGroupLabel.PluralGroup]: dataGroupId
        ? getSelectedGroupLabel({dataGroupId, dataGroups, isPlural: true})
        : `users`,
      [AggregationGroupLabel.PluralGroupTitle]: toTitleCase(
        getSelectedGroupLabel({dataGroupId, dataGroups, isPlural: true}),
      ),
      [AggregationGroupLabel.TimePeriod]: timePeriod,
    };
    return label(replacements);
  }
  return label;
}

function findSelectedAdvancedValue(sections: Section[]) {
  const selectedAdvancedValue: SectionItem = findSelectedItems<Section>(sections, (item) => item.isSelected)?.[0];
  if (selectedAdvancedValue?.subSections) {
    return findSelectedAdvancedValue(selectedAdvancedValue?.subSections);
  }
  return selectedAdvancedValue;
}

export function getSelectedAggregationLabel({
  metricType,
  operator,
  countTypeSelection,
}: {
  metricType: MetricType;
  operator: MATH_TYPE;
  countTypeSelection: SectionItem<CountTypeSelector>;
}) {
  const label = OperatorToString[operator];
  if (label) {
    return label;
  }

  if (metricType === MetricType.Event && countTypeSelection.value.operator === ALL_MATH_TYPES.UNIQUE) {
    return `Select Unique Count`;
  }
  return `Select Aggregation`;
}

export function getSelectedCountByLabel({
  selected,
  metricType,
  dataGroupId,
  dataGroups,
  timePeriod,
  advancedMenuSections,
  property,
}: {
  selected: CountBy;
  metricType: MetricType;
  dataGroupId: string;
  dataGroups: DataGroup[];
  timePeriod: string;
  advancedMenuSections?: Array<Section<AdvancedMenuValue>>;
  property: PropertySectionItem;
}): string {
  const {aggregationOperator, aggregationOperatorPerUser, countType, countUsersOnceType} = selected;
  const label = metricType && CountOutputLabelMappings.get(metricType).get(countType);
  const selectedAdvancedValue = advancedMenuSections && findSelectedAdvancedValue(advancedMenuSections);
  let selectedLabel = ``;
  if (selectedAdvancedValue && metricType !== MetricType.Event) {
    selectedLabel = selectedAdvancedValue.label ? ` on ${selectedAdvancedValue.label}` : ``;
  }

  return (
    formatOutputLabel({
      label,
      aggregationOperator,
      aggregationOperatorPerUser,
      countUsersOnceType,
      property,
      dataGroupId,
      dataGroups,
      timePeriod,
    }) + selectedLabel
  );
}

export function getSubSelectionsForCountType({
  countTypeSelection,
  property,
}: {
  // countTypeSelection coming from constant#INSIGHTS_COUNT_MENU_ITEMS
  countTypeSelection: SectionItem<CountTypeSelector>;
  // property is the selected property in aggregation (if presented)
  property: PropertySectionItem;
}): SubSelectionTypes[] {
  // the requiredSubSelections could be either
  // 1. function takes in {property} as only arg
  // 2. a static array
  // we want to get an array like [`aggregation`, `aggregationPerUser`, `property`, `nestedProperty`]
  // to tell the count menu what sub sections to render.
  // The property is needed here because we conditionally show `nestedProperty` if it is a list of object
  const requiredSubSelections =
    typeof countTypeSelection.value.requiredSubSelections === `function`
      ? countTypeSelection.value.requiredSubSelections({property})
      : countTypeSelection.value.requiredSubSelections || [];
  return [...requiredSubSelections];
}

export function shouldCommitSelection({
  countTypeSelection,
  property = null,
  aggregationOperatorPerUser = null,
  aggregationOperator = null,
  countUsersOnceType = null,
}: {
  countTypeSelection: SectionItem<CountTypeSelector>;
  property?: PropertySectionItem;
  aggregationOperatorPerUser?: MATH_TYPE;
  aggregationOperator?: MATH_TYPE;
  countUsersOnceType?: COUNT_USERS_ONCE_TYPE;
}): boolean {
  const hasRequiredSelections = countTypeSelection.value.hasRequiredSelections;
  const replacements: RequiredSelectionsParams = {
    [SubSelectionTypes.Property]: property,
    [SubSelectionTypes.NestedProperty]: isProperty(property) ? property.listItemGroup : null,
    [SubSelectionTypes.AggregationPerUser]: aggregationOperatorPerUser,
    [SubSelectionTypes.Aggregation]: aggregationOperator,
    [SubSelectionTypes.CountUsersOnce]: countUsersOnceType,
  };

  return hasRequiredSelections(replacements);
}

export function generateSectionItem({
  item,
  dataGroupId,
  dataGroups,
}: {
  item: CountTypeSelector | AggregationSelector;
  dataGroupId: string;
  dataGroups: DataGroup[];
}): SectionItem {
  return {
    label: formatSelectorLabel(item.label, {dataGroupId, dataGroups}),
    shortLabel: formatSelectorLabel(item.shortLabel, {dataGroupId, dataGroups}),
    value: item,
    tooltip: item.description
      ? {
          text: formatSelectorLabel(item.description, {dataGroupId, dataGroups}),
          size: DisplaySize.Small,
          placement: Placement.Right,
        }
      : null,
  };
}

function getSubSelectionHeaderInfo({
  countType,
  subSelectionType,
  dataGroupId,
  dataGroups,
}: {
  countType: CountType;
  subSelectionType: SubSelectionTypes;
  dataGroupId: string;
  dataGroups: DataGroup[];
}) {
  /** To Do: Make this function to be more generic - support Funnel and Retention as well */
  const subHeader = InsightsCountSubMenuHeader?.[countType]?.[subSelectionType];
  if (subHeader) {
    const {label, description, icon} = subHeader;
    return {
      label: formatSelectorLabel(label, {dataGroupId, dataGroups}),
      tooltip: {
        text: formatSelectorLabel(description, {dataGroupId, dataGroups}),
        size: DisplaySize.Small,
        placement: Placement.RightCenter,
      },
      icon,
    };
  }
  return null;
}

function createAggregationsSection({
  aggregations,
  label,
  icon,
  tooltip,
  dataGroupId,
  dataGroups,
}: {
  aggregations: AggregationSelector[];
  label?: string;
  icon?: string;
  tooltip?: TooltipInfo;
  dataGroupId?: string;
  dataGroups?: DataGroup[];
}): Array<Section<AggregationSelector>> {
  const aggregationItems: Array<SectionItem<AggregationSelector>> = aggregations.map((aggregation) => {
    return {
      ...generateSectionItem({item: aggregation, dataGroupId, dataGroups}),
      subSections: aggregation.subSections
        ? createAggregationsSection({
            aggregations: aggregation.subSections,
            dataGroupId,
            dataGroups,
          })
        : null,
    };
  });
  return aggregationItems.length ? [{items: aggregationItems, label, icon, tooltip}] : [];
}

export function getAggregationsSectionForCountType({
  countTypeSelections,
  countTypeIndex,
  subSelectionType,
  aggregationOperator,
  property,
  dataGroupId,
  dataGroups,
}: {
  countTypeSelections: Array<SectionItem<CountTypeSelector>>;
  countTypeIndex: number;
  property: PropertySectionItem;
  subSelectionType: SubSelectionTypes;
  aggregationOperator?: MATH_TYPE;
  dataGroupId: string;
  dataGroups: DataGroup[];
}): Array<Section<AggregationSelector>> {
  const countTypeSelection = countTypeSelections[countTypeIndex];
  const {countType, aggregations, requiredSubSelections: requiredSubSelectionsOrFn} = countTypeSelection.value;
  const headerInfo = getSubSelectionHeaderInfo({countType, subSelectionType, dataGroupId, dataGroups});

  let label, tooltip, icon;
  if (headerInfo) {
    label = headerInfo.label;
    tooltip = headerInfo.tooltip;
    icon = headerInfo.icon;
  }
  if (!aggregations) return [];
  const requiredSubSelections =
    typeof requiredSubSelectionsOrFn === `function` ? requiredSubSelectionsOrFn({property}) : requiredSubSelectionsOrFn;
  if (requiredSubSelections.includes(SubSelectionTypes.AggregationPerUser) && aggregationOperator) {
    const subAggregations =
      aggregations.find((aggregation) => aggregation.operator === aggregationOperator)?.subAggregations ||
      aggregations
        .find((aggregation) => !!aggregation.subSections)
        ?.subSections?.find((aggregation) => aggregation.operator === aggregationOperator)?.subAggregations;
    return createAggregationsSection({
      aggregations: subAggregations,
      label,
      icon,
      tooltip,
      dataGroupId,
      dataGroups,
    });
  }

  return createAggregationsSection({
    aggregations,
    label,
    icon,
    tooltip,
    dataGroupId,
    dataGroups,
  });
}

function findMatchingAggregation(
  aggregation: AggregationSelector,
  callback: (params: {operator: MATH_TYPE; perUserOperator: MATH_TYPE}) => boolean,
): AggregationSelector {
  if (callback({operator: aggregation.operator, perUserOperator: aggregation.perUserOperator})) {
    return aggregation;
  }
  const aggregations = aggregation.subAggregations || aggregation.subSections || [];
  for (const aggregationItem of aggregations) {
    const matchedItem = findMatchingAggregation(aggregationItem, callback);
    if (matchedItem) return matchedItem;
  }
  return null;
}

/** A tooltip text to be displayed in Aggregation menu header */
export function getSubSelectionDescription({
  searchFirstLevelAggregation,
  countTypeSelection,
  aggregationOperator,
  aggregationOperatorPerUser,
  dataGroupId,
  dataGroups,
}: {
  searchFirstLevelAggregation: boolean;
  countTypeSelection: SectionItem<CountTypeSelector>;
  aggregationOperator: MATH_TYPE;
  aggregationOperatorPerUser: MATH_TYPE;
  dataGroupId: string;
  dataGroups: DataGroup[];
}): string {
  const aggregations = countTypeSelection.value.aggregations;

  let searchCriteria;
  if (searchFirstLevelAggregation) {
    const hasAggregator = Boolean(aggregationOperator);
    searchCriteria = {
      operator: aggregationOperator ?? null,
      perUserOperator: hasAggregator ? null : aggregationOperatorPerUser,
    };
  } else {
    searchCriteria = {
      operator: aggregationOperator ?? null,
      perUserOperator: aggregationOperatorPerUser ?? null,
    };
  }
  const condition = ({operator, perUserOperator}) => {
    return (
      (operator ?? null) === searchCriteria.operator && (perUserOperator ?? null) === searchCriteria.perUserOperator
    );
  };
  for (const aggregation of aggregations) {
    const selectedAggregation = findMatchingAggregation(aggregation, condition);
    const description = selectedAggregation?.description;
    if (description) {
      return formatSelectorLabel(description, {dataGroupId, dataGroups});
    }
  }

  return ``;
}

export function generateInsightsAdvancedCountMenuOptions({
  selectedPlotStyle,
  selectedCalculation,
  selectedDateUnit,
}: {
  selectedPlotStyle: ChartPlotStyle;
  selectedCalculation: Calculation;
  selectedDateUnit: RollingWindowSize;
}) {
  return generateCountCalculationMenuOptions({
    selectedPlotStyle,
    selectedCalculation,
    selectedDateUnit,
  });
}

export function generateCountCalculationMenuOptions({
  selectedPlotStyle,
  selectedCalculation,
  selectedDateUnit,
}: {
  selectedPlotStyle: ChartPlotStyle;
  selectedCalculation: Calculation;
  selectedDateUnit: RollingWindowSize;
}) {
  const {calculationType, rollingWindowSize} = selectedCalculation;
  return [
    {
      label: `Calculation Options`,
      icon: `info-xs`,
      tooltip: {
        size: DisplaySize.Small,
        placement: Placement.RightCenter,
        text: `Calculation overrides based on time. Active chart will become Line or Stacked Line.\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#analysis-settings)`,
      },
      items: [
        {
          label: `Rolling Average`,
          isSelected: calculationType === CalculationTypes.Rolling,
          subSections: [
            {
              items: ROLLING_WINDOW_SIZE_OPTIONS[selectedDateUnit].map((size) => ({
                label: `${size} ${selectedDateUnit}s`,
                value: {
                  calculationType: CalculationTypes.Rolling,
                  rollingWindowSize: size,
                },
                tooltip: {
                  size: DisplaySize.Small,
                  placement: Placement.Right,
                  'image-src': selectedPlotStyle === ChartPlotStyle.Stacked ? rollingStackedLineSvg : rollingLineSvg,
                  text: `Rolling smooths trends by calculating the average value over previous ${size} ${selectedDateUnit}s period`,
                },
                isSelected: size === rollingWindowSize,
              })),
            },
          ],
        },
        {
          label: `Cumulative`,
          isSelected: calculationType === CalculationTypes.Cumulative,
          value: {
            calculationType: CalculationTypes.Cumulative,
          },
          tooltip: {
            size: DisplaySize.Small,
            placement: Placement.Right,
            'image-src': selectedPlotStyle === ChartPlotStyle.Stacked ? cumulativeStackedLineSvg : cumulativeLineSvg,
            text: `Cumulative adds each new period’s value to the sum of previous periods.`,
          },
        },
      ],
    },
  ];
}

export function generateCountUsersOnceMenuOptions({
  selectedCountUsersOnceType,
  timePeriodLabel,
  countUsersOnceState,
}: {
  selectedCountUsersOnceType: COUNT_USERS_ONCE_TYPE;
  timePeriodLabel: string;
  countUsersOnceState: CountUsersOnceState;
}) {
  const items = Object.values(COUNT_USERS_ONCE_TYPE).map((type) => ({
    label: COUNT_USERS_ONCE_OPTIONS[type].label({timePeriod: timePeriodLabel}),
    value: {
      countUsersOnceType: type,
    },
    tooltip: {
      size: DisplaySize.Small,
      placement: Placement.Right,
      text:
        countUsersOnceState === CountUsersOnceState.Required && type === COUNT_USERS_ONCE_TYPE.MULTIPLE
          ? `Count unique users once required for attribution metrics`
          : COUNT_USERS_ONCE_OPTIONS[type].description({timePeriod: timePeriodLabel}),
    },
    isDisabled: !COUNT_USERS_ONCE_OPTIONS[type].enabledStates.includes(countUsersOnceState),
    isSelected: type === selectedCountUsersOnceType,
  }));

  return [
    {
      label: `Count Unique Users`,
      items,
    },
  ];
}

/**
 * Traverses to the bottom most layer of the iterator and returns true if found an aggregation that meets the given condition
 * this is used to perform a pattern matching against the operator and perUserAggregator
 * Boolean(perUserAggregator) -> INSIGHTS_MENU_ITEMS.PER_USER_AGGREGATION
 * operator === TOTAL -> INSIGHTS_MENU_ITEMS.TOTAL_AGGREGATION
 * operator === UNIQUE -> INSIGHTS_MENU_ITEMS.UNIQUE_AGGREGATION
 */
function hasMatchingAggregation(
  aggregation: AggregationSelector,
  callback: (param: {operator: MATH_TYPE; perUserOperator: MATH_TYPE}) => boolean,
) {
  const {operator, perUserOperator} = aggregation;
  const aggregations = aggregation.subAggregations || aggregation.subSections;
  if (!aggregations) {
    return callback({operator, perUserOperator});
  }
  for (const aggregationItem of aggregations) {
    if (hasMatchingAggregation(aggregationItem, callback)) {
      return true;
    }
  }
  return false;
}

function getMeasurementsFromEntry(
  entry: QueryMetricEntry | MetricEntry,
): {
  aggregationOperator: MATH_TYPE;
  aggregationOperatorPerUser: MATH_TYPE;
  property: PropertySectionItem;
  rolling: {rollingWindowSize: number} | null;
  cumulative: boolean;
  countUsersOnceType: COUNT_USERS_ONCE_TYPE;
} {
  let aggregationOperator: MATH_TYPE;
  let aggregationOperatorPerUser: MATH_TYPE;
  let property: PropertySectionItem;
  let rolling: {rollingWindowSize: number} = null;
  let cumulative = false;
  let countUsersOnceType = null;

  if (isMetricEntry(entry)) {
    aggregationOperator = entry.measurement.aggregationOperator;
    aggregationOperatorPerUser = entry.measurement.aggregationOperatorPerUser;
    property = entry.measurement.property;
    rolling = entry.measurement.rolling;
    cumulative = entry.measurement.cumulative;
  } else {
    aggregationOperator = entry.aggregationOperator;
    aggregationOperatorPerUser = entry.aggregationOperatorPerUser;
    property = entry.property;
    rolling = entry.rolling;
    cumulative = entry.cumulative;
    countUsersOnceType = entry.countUsersOnceType;
  }
  return {
    aggregationOperator,
    aggregationOperatorPerUser,
    property,
    rolling,
    cumulative,
    countUsersOnceType,
  };
}

/**
 * Returns the count type for a given entry
 */
function getCountTypeForEntry(entry: QueryMetricEntry | MetricEntry): CountType {
  const {aggregationOperator, aggregationOperatorPerUser, property} = getMeasurementsFromEntry(entry);

  const condition = ({operator, perUserOperator}) => {
    return (operator ?? null) === aggregationOperator && (perUserOperator ?? null) === aggregationOperatorPerUser;
  };

  for (const countMenuItem of INSIGHTS_COUNT_MENU_ITEMS) {
    const {countType, forProperty, operator, perUserOperator, aggregations} = countMenuItem;
    if (Boolean(forProperty) === Boolean(property)) {
      if (aggregations) {
        for (const aggregation of aggregations) {
          if (hasMatchingAggregation(aggregation, condition)) return countType;
        }
      } else {
        if (condition({operator, perUserOperator})) {
          return countType;
        }
      }
    }
  }
  return null;
}

export function getSelectedCountBy(entry: QueryMetricEntry | MetricEntry): CountBy {
  const {
    aggregationOperator,
    aggregationOperatorPerUser,
    property,
    rolling,
    cumulative,
    countUsersOnceType,
  } = getMeasurementsFromEntry(entry);

  const countType = getCountTypeForEntry(entry);
  const advancedMenuValue: Calculation = {
    calculationType: null,
  };
  if (rolling) {
    advancedMenuValue.calculationType = CalculationTypes.Rolling;
    advancedMenuValue[`rollingWindowSize`] = rolling.rollingWindowSize;
  } else if (cumulative) {
    advancedMenuValue.calculationType = CalculationTypes.Cumulative;
  }

  const countBy: CountBy = {
    countType,
    aggregationOperator,
    aggregationOperatorPerUser,
    property,
    countUsersOnceType,
    advancedMenuValue,
  };

  return countBy;
}
